import React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import currency from 'currency.js';

import { getPaymentListingColumns } from '@containers/payments/PaymentLandingPage/PaymentListingColumn';
import PaymentPortalPaymentDetails from '@containers/payments/PaymentLandingPage/PaymentPortalPaymentDetails';

import Button from '@components/Button/Button';
import Loading from '@components/Loading';
import Table from '@components/Table';

import { getHostedPaymentPageToken, HOSTED_PAYMENT_PAGE_TOKEN_SUCCESS } from '@actions/paymentPortal';

import { getCurrency, getDecimalCount, getEmptyErrorState, isProductionEnv } from '@utils/AppUtil';
import { getTransactionFeeAmount } from '@utils/PaymentUtil';

import './style.scss';

export interface IPaymentPortalListingProps {
  config: IPaymentPortalConfig;
  paymentPortalBalanceListing: IPaymentPortalBalanceListing;
  caseNumber: string;
  portalToken: string;
  show: boolean;
  // redux
  getHostedPaymentPageToken: TGetHostedPaymentPageToken;
  paymentPortalJWTToken: string;
}

export interface IPaymentPortalListingState {
  hostedPaymentPageToken: string;
  fetchingHostedPaymentPageToken: boolean;
  paymentAmount: number;
  paymentAmountStr: string;
  paymentAmountError: IFieldValidation;
}

export class PaymentPortalListing extends React.Component<IPaymentPortalListingProps, IPaymentPortalListingState> {
  constructor(props: IPaymentPortalListingProps) {
    super(props);

    this.state = this.getEmptyState();
  }

  componentDidMount() {
    if (this.props.paymentPortalBalanceListing) {
      this.setState({
        paymentAmount: +this.props.paymentPortalBalanceListing.totalOutstandingBalance,
        paymentAmountStr: this.props.paymentPortalBalanceListing.totalOutstandingBalance.toString(),
      });
    }
  }

  getEmptyState = (): IPaymentPortalListingState => ({
    hostedPaymentPageToken: null,
    paymentAmount: null,
    paymentAmountStr: null,
    paymentAmountError: getEmptyErrorState(),
    fetchingHostedPaymentPageToken: false,
  });

  handleAmountChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    if (isNaN(+value)) {
      return;
    }
    if (getDecimalCount(value) <= 2) {
      this.setState((prevState) => ({
        ...prevState,
        paymentAmount: +value,
        paymentAmountStr: value.toString(),
      }));
    }
  };

  renderPaymentDetails = () => {
    const { config } = this.props;
    const { paymentAmount, paymentAmountStr, paymentAmountError } = this.state;

    return (
      <PaymentPortalPaymentDetails
        config={config}
        paymentAmount={paymentAmount}
        paymentAmountStr={paymentAmountStr}
        paymentAmountError={paymentAmountError}
        handleChange={this.handleAmountChange}
      />
    );
  };

  validateForm = () => {
    const { paymentAmount } = this.state;
    const {
      config: { minimumPaymentAmount },
      paymentPortalBalanceListing: { totalOutstandingBalance },
    } = this.props;
    const paymentAmountError = getEmptyErrorState();
    let isValid = true;

    if (!+paymentAmount || +paymentAmount < +minimumPaymentAmount || +paymentAmount > totalOutstandingBalance) {
      paymentAmountError.errorState = IValidationState.ERROR;
      // tslint:disable-next-line: max-line-length
      paymentAmountError.errorMessage = `Min $${+minimumPaymentAmount} and Max ${getCurrency(
        totalOutstandingBalance
      )} amount limit.`;

      isValid = false;
    }

    this.setState({ paymentAmountError });

    return isValid;
  };

  onEnterPaymentDetails = () => {
    if (this.validateForm()) {
      const { paymentAmount } = this.state;
      const {
        portalToken,
        paymentPortalJWTToken,
        config,
        config: { includeTransactionFee },
      } = this.props;

      const transactionFee = getTransactionFeeAmount(config, paymentAmount);

      const hostedPaymentPageRequest: IHosetedPaymentPageRequest = {
        amount: currency(paymentAmount).value,
        paymentPortalToken: portalToken,
        transactionFee: includeTransactionFee ? transactionFee : null,
      };

      this.setState({ fetchingHostedPaymentPageToken: true }, () => {
        this.props
          .getHostedPaymentPageToken(paymentPortalJWTToken, hostedPaymentPageRequest)
          .then((result) => {
            if (result.type === HOSTED_PAYMENT_PAGE_TOKEN_SUCCESS) {
              this.setState({ hostedPaymentPageToken: result.response.token }, () => {
                document.getElementById('btnContinue').click();
              });
            } else {
              this.setState({ fetchingHostedPaymentPageToken: false });
            }
          })
          .catch(() => this.setState({ fetchingHostedPaymentPageToken: false }));
      });
    }
  };

  renderCollectionStatus = () => (
    <div className="payment-portal-home__collection-status-header">
      <header className="text-center">
        <div className="img-danger-transaction" />
        <h1>Your Case Balance Has Been Sent to Collections</h1>
        <p>
          Your outstanding balance has been sent to a collections agency, which will reach out to you regarding next
          steps.
        </p>
      </header>
    </div>
  );

  renderPaymentPortalBalanceListing = () => {
    const { config, caseNumber, paymentPortalBalanceListing } = this.props;

    const columns = getPaymentListingColumns();

    const { transactions: rows } = this.props.paymentPortalBalanceListing;

    return (
      <main className="payment-portal-home__payment-info">
        <section className="payment-info">
          {!paymentPortalBalanceListing.isBalanceStatusInCollection && (
            <header className="section-title">
              <h1>Review Your Outstanding Fines & Fees</h1>
            </header>
          )}
          <div className="payment-description">
            <p>Case Number: {caseNumber}</p>
          </div>
        </section>
        {rows && rows.length ? (
          <div className={classnames('fee-details')}>
            <Table
              columns={columns}
              rows={rows}
              plainTable={true}
              showPagination={false}
              resizable={true}
              pageSize={rows.length}
            />
          </div>
        ) : (
          <div className={classnames('no-fines')}>Case has $0 outstanding balance, no payment required.</div>
        )}
        {rows && rows.length ? (
          <header className="outstanding-balance-details">
            <div className="label">Outstanding Balance</div>
            <div className="balance">{currency(paymentPortalBalanceListing.totalOutstandingBalance).format()}</div>
          </header>
        ) : null}
        {rows && rows.length && !paymentPortalBalanceListing.isBalanceStatusInCollection
          ? this.renderPaymentDetails()
          : null}
        {rows && rows.length && !paymentPortalBalanceListing.isBalanceStatusInCollection ? (
          <>
            <div className="payment-details-btn-container">
              <Button
                bsStyle={ButtonStyle.PRIMARY}
                onClick={this.onEnterPaymentDetails}
                className="payment-details-btn"
                content="Enter Payment Details"
                bgColor={config.buttonBackgroundColor}
              />
            </div>
            <form
              method="post"
              action={
                isProductionEnv()
                  ? 'https://accept.authorize.net/payment/payment'
                  : 'https://test.authorize.net/payment/payment'
              }
              id="formAuthorizeNetPage"
              name="formAuthorizeNetPage"
            >
              <input type="hidden" name="token" value={this.state.hostedPaymentPageToken} />
              <button id="btnContinue" style={{ visibility: 'hidden' }}>
                Continue to next page
              </button>
            </form>
          </>
        ) : (
          <div className="collection-status-footer text-center">
            {config.originWebsiteUrl && (
              <a href={config.originWebsiteUrl} className="return-anchor-button">
                Return to Municipal Site
              </a>
            )}
          </div>
        )}
      </main>
    );
  };

  render() {
    const { show, paymentPortalBalanceListing } = this.props;
    const { fetchingHostedPaymentPageToken } = this.state;

    return (
      <div className="payment-portal-home">
        {paymentPortalBalanceListing.isBalanceStatusInCollection && this.renderCollectionStatus()}
        {show && this.renderPaymentPortalBalanceListing()}
        <Loading show={fetchingHostedPaymentPageToken} />
      </div>
    );
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  paymentPortalJWTToken: state.paymentPortal.paymentPortalJWTToken,
});

const mapDispatchToProps = (dispatch: any) => ({
  getHostedPaymentPageToken: (paymentPortalJWTToken: string, pageRequest: IHosetedPaymentPageRequest) =>
    dispatch(getHostedPaymentPageToken(paymentPortalJWTToken, pageRequest)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PaymentPortalListing);
